/*
 * rg-saved-2026-10-17 — Revgear wishlist + save-for-later (GH #175).
 * Plan: claudedocs/plans/wishlist-save-for-later-plan.md §3.1, §3.2, §4.1, §4.3, §4.4, §5, §6.
 *
 * ONE store, ONE document (plan §3.1) in localStorage `rg_saved_v1`. Every surface — hearts,
 * the header count, "Saved for later" in the cart drawer and on /cart, the wishlist page and
 * the list picker — reads the store and repaints on the `rg:saved:change` event; no component
 * owns state.
 *
 * Loaded by layout/theme.liquid ONLY while the wishlist launch switch is on (rg-saved-enabled:
 * shop flag or the `wishlist-beta` tag; B2B saves price-less since 2026-10-19). Account sync (B5, `syncQueue` below:
 * debounced POST to the Shopify app proxy /apps/revgear/saved, one request in flight, `rev`
 * ordering, sendBeacon on pagehide) runs only for a logged-in customer AND while the separate
 * shop flag revgear.saved_sync_enabled is true; with that flag off it makes no network call.
 *
 * Document contract (shared with the B4 server merge, `_saved-doc.ts` in the Revgear Tools
 * repo): items keyed by `vid` per bucket (`sfl` or a list id); every remove writes a tombstone
 * `{k, at}` — `sfl:<vid>`, `<listId>:<vid>`, or `<listId>` for a whole list; an item or list
 * survives a tombstone only when its `at` is strictly newer; a list's `at` changes on rename and
 * on default change; `rev` is bumped once per mutation batch here and never by the server.
 */
(() => {
  'use strict';

  if (window.rgSaved) return;

  const theme = window.theme || {};
  const S = (theme.strings && theme.strings.wishlist) || {};
  const routes = theme.routes || {};
  // rg-saved-2026-10-19: a wholesale (B2B) session saves items WITHOUT a price (company prices are
  // contextual, Jason 2026-10-19): no `p` / `cur` on new records, no `_rg_p` stamp, no price notices.
  const B2B = !!(theme.info && theme.info.rgSavedB2b);

  const KEY = 'rg_saved_v1';
  const LIMITS = { lists: 10, perList: 100, sfl: 50, total: 350, tombstones: 100, tombstoneDays: 90, name: 40, qty: 999 };
  const LIST_ID = /^l_[a-z0-9]{1,32}$/;
  const TOMBSTONE_KEY = /^(?:(?:sfl|l_[a-z0-9]{1,32}):[1-9]\d{0,19}|l_[a-z0-9]{1,32})$/;
  // eslint-disable-next-line no-control-regex
  const CONTROL = /[\u0000-\u001f\u007f]/;

  // ── Small helpers ──────────────────────────────────────────────────────────────────────

  /** A string from theme.strings.wishlist with [placeholders] filled in. */
  const t = (key, vars) => {
    let s = typeof S[key] === 'string' ? S[key] : '';
    if (vars) {
      Object.keys(vars).forEach((k) => { s = s.split(`[${k}]`).join(String(vars[k])); });
    }
    return s;
  };
  const plural = (n, oneKey, otherKey) => (n === 1 ? t(oneKey, { count: n }) : t(otherKey, { count: n }));

  // Strictly increasing timestamps: a remove followed by a re-add in the same millisecond must
  // still give the re-add a newer `at` than its tombstone, or the server merge would drop it.
  let lastMs = 0;
  const nowIso = () => {
    let now = Date.now();
    if (now <= lastMs) now = lastMs + 1;
    lastMs = now;
    return new Date(now).toISOString();
  };
  const msOf = (iso) => {
    const v = typeof iso === 'string' ? Date.parse(iso) : NaN;
    return Number.isNaN(v) ? 0 : v;
  };
  const toInt = (v) => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isSafeInteger(n) ? n : NaN;
  };
  const clone = (o) => JSON.parse(JSON.stringify(o));
  const nameKey = (name) => String(name).trim().toLocaleLowerCase('en-US');

  /** Money in the shop's own format (the same template Liquid's `money` filter uses). */
  function formatMoney(cents) {
    const format = typeof S.moneyFormat === 'string' && S.moneyFormat ? S.moneyFormat : '${{amount}}';
    const fmt = (number, precision, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(cents, 2, ' ', '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]*>/g, '');
  }

  // ── Document shape: normalize, prune, merge (mirror of the B4 server rules) ─────────────

  const emptyDoc = () => ({ v: 1, rev: 0, updatedAt: nowIso(), sfl: [], lists: [], del: [] });

  function cleanItem(raw, isSfl) {
    if (!raw || typeof raw !== 'object') return null;
    const vid = toInt(raw.vid);
    const pid = toInt(raw.pid);
    if (!(vid > 0) || !(pid > 0)) return null;
    // Price: both `p` and `cur`, or neither (a wholesale save) — never half.
    const hasP = raw.p !== undefined && raw.p !== null;
    const hasCur = raw.cur !== undefined && raw.cur !== null;
    if (hasP !== hasCur) return null;
    const p = hasP ? toInt(raw.p) : undefined;
    if (hasP && !(p >= 0)) return null;
    if (hasCur && (typeof raw.cur !== 'string' || !/^[A-Z]{3}$/.test(raw.cur))) return null;
    if (typeof raw.h !== 'string' || !raw.h.trim() || raw.h.length > 255) return null;
    const at = msOf(raw.at);
    if (!at) return null;
    const h = raw.h.trim();
    const atIso = new Date(at).toISOString();
    const priced = hasP ? { p, cur: raw.cur } : {};
    if (isSfl) {
      const q = toInt(raw.q);
      return { vid, pid, h, q: q >= 1 && q <= LIMITS.qty ? q : 1, ...priced, at: atIso };
    }
    return { vid, pid, h, ...priced, at: atIso };
  }

  /** One record per vid — the EARLIEST `at` wins (and with it the earlier baseline `p`). */
  function dedupeItems(items) {
    const byVid = new Map();
    items.forEach((item) => {
      const existing = byVid.get(item.vid);
      if (!existing || msOf(item.at) < msOf(existing.at)) byVid.set(item.vid, item);
    });
    return [...byVid.values()].sort((a, b) => msOf(a.at) - msOf(b.at) || a.vid - b.vid);
  }

  function dedupeDel(del) {
    const byKey = new Map();
    del.forEach((d) => {
      const existing = byKey.get(d.k);
      if (!existing || msOf(d.at) > msOf(existing.at)) byKey.set(d.k, d);
    });
    return [...byKey.values()];
  }

  function pruneDel(del) {
    const cutoff = Date.now() - LIMITS.tombstoneDays * 86400000;
    return dedupeDel(del)
      .filter((d) => msOf(d.at) >= cutoff)
      .sort((a, b) => msOf(b.at) - msOf(a.at) || a.k.localeCompare(b.k))
      .slice(0, LIMITS.tombstones);
  }

  /** Exactly one default list: the most recently changed of those flagged, else the first. */
  function fixDefault(doc) {
    if (!doc.lists.length) return;
    const flagged = doc.lists.filter((l) => l.def);
    const keep = flagged.length
      ? flagged.reduce((best, l) => (msOf(l.at) > msOf(best.at) ? l : best))
      : doc.lists[0];
    doc.lists.forEach((l) => { l.def = l === keep; });
  }

  /** Validates a document from storage or the server island; invalid entries are dropped. */
  function normalize(raw) {
    if (!raw || typeof raw !== 'object' || raw.v !== 1) return null;
    const rev = toInt(raw.rev);
    const doc = {
      v: 1,
      rev: rev >= 0 ? rev : 0,
      updatedAt: msOf(raw.updatedAt) ? new Date(msOf(raw.updatedAt)).toISOString() : nowIso(),
      sfl: [],
      lists: [],
      del: []
    };
    doc.sfl = dedupeItems((Array.isArray(raw.sfl) ? raw.sfl : []).map((i) => cleanItem(i, true)).filter(Boolean));
    const seen = new Set();
    (Array.isArray(raw.lists) ? raw.lists : []).forEach((l) => {
      if (!l || typeof l !== 'object' || typeof l.id !== 'string' || !LIST_ID.test(l.id) || seen.has(l.id)) return;
      const name = typeof l.name === 'string' ? l.name.trim() : '';
      if (!name || name.length > 100 || CONTROL.test(name) || !msOf(l.at)) return;
      seen.add(l.id);
      doc.lists.push({
        id: l.id,
        name,
        def: l.def === true,
        at: new Date(msOf(l.at)).toISOString(),
        items: dedupeItems((Array.isArray(l.items) ? l.items : []).map((i) => cleanItem(i, false)).filter(Boolean))
      });
    });
    doc.del = dedupeDel((Array.isArray(raw.del) ? raw.del : [])
      .filter((d) => d && typeof d.k === 'string' && TOMBSTONE_KEY.test(d.k) && msOf(d.at))
      .map((d) => ({ k: d.k, at: new Date(msOf(d.at)).toISOString() })));
    fixDefault(doc);
    return doc;
  }

  /**
   * Client copy of the server merge (§4.3, `mergeSavedDocuments` in _saved-doc.ts): lists match by
   * id, then by case-insensitive name; tombstones union (newest per key); an item / list survives
   * only when its `at` is newer than its tombstone; duplicates keep the earlier `at`; rev = max.
   */
  function mergeDocs(stored, incoming) {
    const storedById = new Map(stored.lists.map((l) => [l.id, l]));
    const idMap = new Map();
    const claimed = new Set();
    incoming.lists.forEach((l) => {
      if (storedById.has(l.id)) { idMap.set(l.id, l.id); claimed.add(l.id); }
    });
    incoming.lists.forEach((l) => {
      if (idMap.has(l.id)) return;
      const match = stored.lists.find((c) => !claimed.has(c.id) && nameKey(c.name) === nameKey(l.name));
      if (match) { idMap.set(l.id, match.id); claimed.add(match.id); }
    });
    const remap = (k) => {
      if (k.startsWith('sfl:')) return k;
      const [listId, vid] = k.split(':');
      const mapped = idMap.get(listId) || listId;
      return vid === undefined ? mapped : `${mapped}:${vid}`;
    };
    const mergedDel = dedupeDel([...stored.del, ...incoming.del.map((d) => ({ k: remap(d.k), at: d.at }))]);
    const tombs = new Map(mergedDel.map((d) => [d.k, d]));
    const survives = (at, tomb) => !tomb || msOf(at) > msOf(tomb.at);
    const bucket = (key, a, b) => dedupeItems([...a, ...b].filter((i) => survives(i.at, tombs.get(`${key}:${i.vid}`))));

    const incomingByTarget = new Map(incoming.lists.map((l) => [idMap.get(l.id) || l.id, l]));
    const order = [
      ...stored.lists.map((l) => l.id),
      ...incoming.lists.map((l) => idMap.get(l.id) || l.id).filter((id) => !storedById.has(id))
    ];
    const lists = [];
    order.forEach((id) => {
      const s = storedById.get(id);
      const i = incomingByTarget.get(id);
      const meta = s && i ? (msOf(i.at) >= msOf(s.at) ? i : s) : (i || s);
      if (!survives(meta.at, tombs.get(id))) return;
      lists.push({ id, name: meta.name, def: meta.def, at: meta.at, items: bucket(id, s ? s.items : [], i ? i.items : []) });
    });
    const merged = {
      v: 1,
      rev: Math.max(stored.rev, incoming.rev),
      updatedAt: nowIso(),
      sfl: bucket('sfl', stored.sfl, incoming.sfl),
      lists,
      del: pruneDel(mergedDel)
    };
    fixDefault(merged);
    return merged;
  }

  const sameContent = (a, b) => JSON.stringify({ ...a, updatedAt: '' }) === JSON.stringify({ ...b, updatedAt: '' });

  // ── Storage + the B5 sync hook ──────────────────────────────────────────────────────────

  function readLocal() {
    try {
      const raw = window.localStorage.getItem(KEY);
      return raw ? normalize(JSON.parse(raw)) : null;
    } catch (e) {
      return null; // private mode, blocked storage or a corrupt value: start empty
    }
  }

  function writeLocal(doc) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(doc));
    } catch (e) {
      // Quota or blocked storage: the store keeps working in memory for this page.
    }
  }

  // rg-saved-sync-2026-10-17 (B5): small storage helpers for the sync bookkeeping. The document
  // shape is untouched; who owns the local copy lives under its own key.
  const OWNER_KEY = 'rg_saved_owner_v1'; // {o: hashed customer id, d: 1 = local changes not yet synced}
  const MERGED_KEY = 'rg_saved_merged'; // sessionStorage: owner hash whose login merge is done

  function readOwner() {
    try {
      const raw = window.localStorage.getItem(OWNER_KEY);
      const v = raw ? JSON.parse(raw) : null;
      return v && typeof v.o === 'string' && v.o ? { o: v.o, d: v.d === 1 ? 1 : 0 } : null;
    } catch (e) {
      return null;
    }
  }
  function writeOwner(v) {
    try {
      if (v) window.localStorage.setItem(OWNER_KEY, JSON.stringify(v));
      else window.localStorage.removeItem(OWNER_KEY);
    } catch (e) { /* blocked storage */ }
  }
  function clearLocal() {
    try { window.localStorage.removeItem(KEY); } catch (e) { /* blocked storage */ }
    writeOwner(null);
    try { window.sessionStorage.removeItem(MERGED_KEY); } catch (e) { /* blocked storage */ }
  }
  const sessionGet = (k) => { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } };
  const sessionSet = (k, v) => { try { window.sessionStorage.setItem(k, v); } catch (e) { /* blocked */ } };
  const debug = (...args) => { try { if (window.console && console.debug) console.debug('[rg-saved sync]', ...args); } catch (e) { /* none */ } };
  /** Same lists, items and tombstones (ignores rev and updatedAt). */
  const sameItems = (a, b) => JSON.stringify({ ...a, rev: 0, updatedAt: '' }) === JSON.stringify({ ...b, rev: 0, updatedAt: '' });

  /**
   * rg-saved-sync-2026-10-17 (B5, plan §4.2): account sync through the Shopify app proxy.
   *
   * Runs only when `enable()` is called by hydrateFromServer — i.e. a logged-in customer, the
   * wishlist switch on, AND the shop flag revgear.saved_sync_enabled true (data-rg-sync on the
   * #rg-saved-server island). Otherwise mark() only flips an in-memory flag: no network at all.
   *
   * - Every mutation marks dirty; 750 ms debounce; POST the whole document to /apps/revgear/saved
   *   (Shopify signs it and adds the customer id). One request in flight; later mutations wait.
   * - 200: the canonical document replaces the local copy (rev = max) — unless the local copy
   *   changed while the request was out (newer local rev / mutation), then the response is
   *   ignored and the newer local state is sent next.
   * - 409, 5xx, 429, timeout, network error, non-JSON body: keep local; retry on the next
   *   mutation or page load, never sooner than one minute after a failure; after 3 failures stop
   *   for this page. 401 / 404: stop for this page silently (logged out / proxy missing).
   * - 413 / 422: keep local, wait for the next mutation; cap errors show the "list full"
   *   message; a stored document in the body is merged locally (§4.3).
   * - pagehide while dirty: navigator.sendBeacon with the current document.
   * Shoppers never see raw errors; details go to console.debug.
   */
  const SYNC_URL = '/apps/revgear/saved';
  const SYNC = { debounce: 750, timeout: 12000, retryGap: 60000, maxFailures: 3 };
  const CAP_CODES = {
    list_full: 'list_full',
    too_many_lists: 'lists_full',
    saved_for_later_full: 'sfl_full',
    too_many_items: 'total_full',
    document_too_large: 'total_full'
  };

  const syncQueue = {
    enabled: false,
    stopped: false,
    dirty: false,
    waitForMutation: false,
    inFlight: false,
    timer: null,
    failures: 0,
    retryAt: 0,
    seq: 0, // bumped on every local change, so a response can tell the copy moved on
    owner: '',

    /** Called by mutate() after every change. */
    mark() {
      this.seq += 1;
      this.dirty = true;
      if (!this.enabled || this.stopped) return;
      this.waitForMutation = false;
      this.persist(1);
      this.schedule(SYNC.debounce);
    },

    persist(d) {
      if (!this.enabled || !this.owner) return;
      writeOwner({ o: this.owner, d });
    },

    schedule(ms) {
      if (!this.enabled || this.stopped) return;
      if (this.timer) window.clearTimeout(this.timer);
      const wait = Math.max(ms, this.retryAt - Date.now(), 0);
      this.timer = window.setTimeout(() => { this.timer = null; this.flush(); }, wait);
    },

    /** Turns sync on for this page (logged in + both switches on). */
    enable(owner, needsPost) {
      this.enabled = true;
      this.owner = owner;
      if (needsPost) {
        this.dirty = true;
        this.persist(1);
        this.schedule(SYNC.debounce);
      } else {
        const o = readOwner();
        this.persist(o && o.o === owner ? o.d : 0);
      }
    },

    stop(reason) {
      this.stopped = true;
      if (this.timer) { window.clearTimeout(this.timer); this.timer = null; }
      debug('stopped for this page:', reason);
    },

    flush() {
      if (!this.enabled || this.stopped || !this.dirty || this.waitForMutation || this.inFlight) return;
      if (Date.now() < this.retryAt) { this.schedule(0); return; }
      const sentSeq = this.seq;
      const sentRev = doc.rev;
      let body;
      try { body = JSON.stringify(doc); } catch (e) { return; }
      this.dirty = false;
      this.inFlight = true;
      this.send(body)
        .then((r) => this.handle(r, sentSeq, sentRev))
        .catch((e) => this.fail(e && e.name === 'AbortError' ? 'timeout' : 'network', sentSeq))
        .then(() => {
          this.inFlight = false;
          if (this.dirty && !this.stopped && !this.waitForMutation) this.schedule(0);
        });
    },

    /** POST with a timeout; resolves {status, json} (json null when the body is not JSON). */
    send(body) {
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctrl ? window.setTimeout(() => ctrl.abort(), SYNC.timeout) : null;
      return fetch(SYNC_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body,
        signal: ctrl ? ctrl.signal : undefined
      })
        .then((res) => res.text().then((text) => {
          let json = null;
          try { json = JSON.parse(text); } catch (e) { json = null; }
          return { status: res.status, json: json && typeof json === 'object' ? json : null };
        }))
        .finally(() => { if (timer) window.clearTimeout(timer); });
    },

    handle(r, sentSeq, sentRev) {
      const { status, json } = r;
      if (status === 200) {
        const server = json && json.ok !== false ? normalize(json.document) : null;
        if (!server) return this.fail('non-JSON or invalid 200 body', sentSeq);
        if (this.seq !== sentSeq) {
          // The local copy changed while this request was out: never roll it back. The newer
          // copy goes out next and its response carries everything this one had.
          debug('response ignored: local copy changed since send', { sent: sentRev, got: server.rev, local: doc.rev });
          this.dirty = true;
          return undefined;
        }
        if (server.rev < doc.rev) {
          // The server echoes rev = max(stored, sent), so an older rev means a wrong response.
          return this.fail(`stale rev ${server.rev} < ${doc.rev}`, sentSeq);
        }
        this.failures = 0;
        this.retryAt = 0;
        sessionSet(MERGED_KEY, this.owner);
        server.rev = Math.max(server.rev, doc.rev);
        if (!sameContent(server, doc)) {
          doc = server;
          writeLocal(doc);
          emit('sync');
        }
        if (!this.dirty) this.persist(0);
        return undefined;
      }
      this.dirty = true;
      if (status === 401 || status === 404) { this.stop(`HTTP ${status}`); return undefined; }
      if (status === 409 || status === 429 || status >= 500) return this.fail(`HTTP ${status}`, sentSeq);
      // 413 / 422 / other 4xx: the server will refuse this document until it changes.
      this.waitForMutation = this.seq === sentSeq;
      const code = json && typeof json.error === 'string' ? json.error : '';
      debug(`HTTP ${status}`, code);
      if (json && json.document) {
        const stored = normalize(json.document);
        if (stored) {
          const merged = mergeDocs(stored, doc);
          if (!sameContent(merged, doc)) {
            this.seq += 1;
            doc = merged;
            writeLocal(doc);
            emit('sync');
          }
        }
      }
      if (CAP_CODES[code]) announce(limitMessage(CAP_CODES[code]));
      return undefined;
    },

    /** A retryable failure: keep local, wait at least a minute, give up after three. */
    fail(reason, sentSeq) {
      this.dirty = true;
      this.failures += 1;
      this.retryAt = Date.now() + SYNC.retryGap;
      debug('failed:', reason, `(${this.failures}/${SYNC.maxFailures})`);
      if (this.failures >= SYNC.maxFailures) { this.stop('too many failures'); return; }
      // Retry on the next mutation; one that arrived while this request was out counts.
      this.waitForMutation = this.seq === sentSeq;
    },

    /** pagehide: hand the unsynced document to the browser so it survives the navigation. */
    beacon() {
      if (!this.enabled || this.stopped || !this.dirty || this.waitForMutation) return;
      if (this.timer) { window.clearTimeout(this.timer); this.timer = null; }
      const nav = window.navigator;
      if (!nav || typeof nav.sendBeacon !== 'function') return;
      try {
        // text/plain keeps the beacon a simple request; the endpoint parses the body as JSON
        // whatever the content type. The outcome is unknown, so `d` stays 1 and the next page
        // load re-sends (an idempotent merge on the server).
        nav.sendBeacon(SYNC_URL, new Blob([JSON.stringify(doc)], { type: 'text/plain;charset=UTF-8' }));
      } catch (e) { /* the next page load retries */ }
    }
  };

  window.addEventListener('pagehide', () => syncQueue.beacon());

  let doc = readLocal() || emptyDoc();

  function emit(reason) {
    document.dispatchEvent(new CustomEvent('rg:saved:change', { detail: { reason } }));
  }

  // ── Store: reads ────────────────────────────────────────────────────────────────────────

  const findList = (d, id) => d.lists.find((l) => l.id === id) || null;
  const bucketItems = (d, bucketId) => {
    if (bucketId === 'sfl') return d.sfl;
    const list = findList(d, bucketId);
    return list ? list.items : null;
  };
  const matches = (item, match) => (match.vid ? item.vid === match.vid : item.pid === match.pid);
  const totalItems = (d) => d.sfl.length + d.lists.reduce((n, l) => n + l.items.length, 0);

  // ── Store: mutation machinery ───────────────────────────────────────────────────────────

  /**
   * Runs `fn` on a draft copy. A result with ok:false or noop:true changes nothing; otherwise the
   * draft becomes the document, `rev` goes up by one (one batch = one user action), tombstones are
   * pruned, it is written to localStorage, marked for sync and announced.
   */
  function mutate(reason, fn) {
    const draft = clone(doc);
    const res = fn(draft) || { ok: true };
    if (res.ok === false || res.noop) return res;
    draft.rev = (toInt(doc.rev) || 0) + 1;
    draft.updatedAt = nowIso();
    draft.del = pruneDel(draft.del);
    fixDefault(draft);
    doc = draft;
    writeLocal(doc);
    syncQueue.mark(reason);
    emit(reason);
    return res;
  }

  function randomId() {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const bytes = new Uint8Array(6);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    return `l_${Array.from(bytes, (b) => alphabet[b % 36]).join('')}`;
  }

  function newListId(d) {
    let id = randomId();
    while (findList(d, id) || d.del.some((x) => x.k === id)) id = randomId();
    return id;
  }

  function tomb(d, k) {
    d.del = d.del.filter((x) => x.k !== k);
    d.del.push({ k, at: nowIso() });
  }

  const untomb = (d, k) => { d.del = d.del.filter((x) => x.k !== k); };

  /** The default list, created lazily ("My Wishlist") on the first heart. */
  function ensureDefault(d) {
    const existing = d.lists.find((l) => l.def) || d.lists[0];
    if (existing) {
      if (!existing.def) { existing.def = true; existing.at = nowIso(); }
      return existing;
    }
    const list = { id: newListId(d), name: t('defaultListName') || 'My Wishlist', def: true, at: nowIso(), items: [] };
    d.lists.push(list);
    return list;
  }

  function validName(d, name, exceptId) {
    const n = typeof name === 'string' ? name.trim() : '';
    if (!n || n.length > LIMITS.name || CONTROL.test(n)) return { ok: false, error: 'name_invalid' };
    if (d.lists.some((l) => l.id !== exceptId && nameKey(l.name) === nameKey(n))) return { ok: false, error: 'name_taken' };
    return { ok: true, name: n };
  }

  /** A new record from page data (heart / cart line); null when the data is incomplete. */
  function makeRecord(info, isSfl, at) {
    const raw = { vid: info.vid, pid: info.pid, h: info.h, at: at || nowIso() };
    if (!B2B) { raw.p = info.p; raw.cur = info.cur; }
    if (isSfl) raw.q = info.q;
    return cleanItem(raw, isSfl);
  }

  /** Adds a record to a bucket, enforcing the §4.4 caps (350 total, 100 per list, 50 SFL). */
  function addRecord(d, bucketId, record, countsTowardTotal = true) {
    const items = bucketItems(d, bucketId);
    if (!items || !record) return { ok: false, error: 'invalid' };
    const existing = items.find((i) => i.vid === record.vid);
    if (existing) {
      if (bucketId === 'sfl') {
        existing.q = Math.min(LIMITS.qty, (existing.q || 1) + (record.q || 1));
        return { ok: true };
      }
      return { ok: true, noop: true, already: true };
    }
    if (bucketId === 'sfl' && items.length >= LIMITS.sfl) return { ok: false, error: 'sfl_full' };
    if (bucketId !== 'sfl' && items.length >= LIMITS.perList) return { ok: false, error: 'list_full' };
    if (countsTowardTotal && totalItems(d) >= LIMITS.total) return { ok: false, error: 'total_full' };
    items.push(record);
    untomb(d, `${bucketId}:${record.vid}`);
    return { ok: true };
  }

  function removeMatching(d, bucketId, match) {
    const items = bucketItems(d, bucketId);
    if (!items) return { ok: false, error: 'invalid' };
    const removed = items.filter((i) => matches(i, match));
    if (!removed.length) return { ok: true, noop: true };
    removed.forEach((i) => {
      items.splice(items.indexOf(i), 1);
      tomb(d, `${bucketId}:${i.vid}`);
    });
    return { ok: true, removed: removed.length };
  }

  const bucketName = (d, bucketId) => (bucketId === 'sfl' ? t('savedForLater') : (findList(d, bucketId) || {}).name || '');

  // ── Store: public API ───────────────────────────────────────────────────────────────────

  const store = {
    LIMITS,
    getDoc: () => clone(doc),
    lists: () => clone(doc.lists),
    list: (id) => { const l = findList(doc, id); return l ? clone(l) : null; },
    defaultList: () => { const l = doc.lists.find((x) => x.def); return l ? clone(l) : null; },
    sfl: () => clone(doc.sfl),
    items: (bucketId) => { const items = bucketItems(doc, bucketId); return items ? clone(items) : []; },
    item: (bucketId, vid) => { const items = bucketItems(doc, bucketId) || []; const i = items.find((x) => x.vid === vid); return i ? clone(i) : null; },
    has: (bucketId, match) => (bucketItems(doc, bucketId) || []).some((i) => matches(i, match)),
    listsWith: (match) => doc.lists.filter((l) => l.items.some((i) => matches(i, match))).map((l) => l.id),
    /** Header count: distinct variants across all lists (Saved for later is cart-side). */
    count: () => new Set(doc.lists.flatMap((l) => l.items.map((i) => i.vid))).size,
    bucketName: (bucketId) => bucketName(doc, bucketId),

    /** Save to a list (the default list when listId is omitted). */
    save(info, listId) {
      return mutate('save', (d) => {
        const list = listId ? findList(d, listId) : ensureDefault(d);
        if (!list) return { ok: false, error: 'invalid' };
        const res = addRecord(d, list.id, makeRecord(info, false));
        return { ...res, listId: list.id, list: list.name };
      });
    },

    /** Remove items matching {vid} or {pid} from one bucket ('sfl' or a list id). */
    remove(bucketId, match) {
      return mutate('remove', (d) => ({ ...removeMatching(d, bucketId, match), list: bucketName(d, bucketId) }));
    },

    /** Cart line → Saved for later (quantity kept; a repeat save adds the quantity). */
    sflAdd(info) {
      return mutate('sfl-add', (d) => addRecord(d, 'sfl', makeRecord(info, true)));
    },

    createList(name) {
      return mutate('list-create', (d) => {
        if (d.lists.length >= LIMITS.lists) return { ok: false, error: 'lists_full' };
        const v = validName(d, name);
        if (!v.ok) return v;
        const list = { id: newListId(d), name: v.name, def: d.lists.length === 0, at: nowIso(), items: [] };
        d.lists.push(list);
        return { ok: true, listId: list.id, list: list.name };
      });
    },

    renameList(id, name) {
      return mutate('list-rename', (d) => {
        const list = findList(d, id);
        if (!list) return { ok: false, error: 'invalid' };
        const v = validName(d, name, id);
        if (!v.ok) return v;
        if (v.name === list.name) return { ok: true, noop: true };
        list.name = v.name;
        list.at = nowIso(); // B4 contract: `at` = name / default last changed
        return { ok: true, list: list.name };
      });
    },

    setDefault(id) {
      return mutate('list-default', (d) => {
        const list = findList(d, id);
        if (!list) return { ok: false, error: 'invalid' };
        if (list.def) return { ok: true, noop: true };
        d.lists.forEach((l) => {
          const was = l.def;
          l.def = l.id === id;
          if (was !== l.def) l.at = nowIso();
        });
        return { ok: true, list: list.name };
      });
    },

    /** Delete a list (tombstone `<listId>`); optionally move its items into another list first. */
    deleteList(id, moveToId) {
      return mutate('list-delete', (d) => {
        const list = findList(d, id);
        if (!list) return { ok: false, error: 'invalid' };
        if (moveToId) {
          const target = findList(d, moveToId);
          if (!target || target.id === id) return { ok: false, error: 'invalid' };
          for (let i = 0; i < list.items.length; i += 1) {
            const item = list.items[i];
            if (!target.items.some((x) => x.vid === item.vid)) {
              if (target.items.length >= LIMITS.perList) return { ok: false, error: 'list_full' };
              // A fresh `at` so an old tombstone for this item in the target list cannot drop it.
              target.items.push({ ...item, at: nowIso() });
              untomb(d, `${target.id}:${item.vid}`);
            }
          }
        }
        d.lists = d.lists.filter((l) => l.id !== id);
        tomb(d, id);
        if (list.def && d.lists.length) {
          const next = (moveToId && findList(d, moveToId)) || d.lists[0];
          next.def = true;
          next.at = nowIso();
        }
        return { ok: true, list: list.name };
      });
    },

    /** Move or copy one item between buckets ('sfl' or list ids). Keeps its baseline `p`. */
    transfer(vid, fromId, toId, keepSource) {
      return mutate(keepSource ? 'copy' : 'move', (d) => {
        if (fromId === toId) return { ok: true, noop: true };
        const src = bucketItems(d, fromId);
        const dst = bucketItems(d, toId);
        if (!src || !dst) return { ok: false, error: 'invalid' };
        const item = src.find((i) => i.vid === vid);
        if (!item) return { ok: false, error: 'invalid' };
        if (!dst.some((i) => i.vid === vid)) {
          // Fresh `at` (see deleteList); the earlier price baseline `p` travels with the item.
          const priced = item.p === undefined ? {} : { p: item.p, cur: item.cur };
          const record = toId === 'sfl'
            ? { vid: item.vid, pid: item.pid, h: item.h, q: item.q || 1, ...priced, at: nowIso() }
            : { vid: item.vid, pid: item.pid, h: item.h, ...priced, at: nowIso() };
          const res = addRecord(d, toId, record, keepSource);
          if (!res.ok) return res;
        }
        if (!keepSource) {
          src.splice(src.indexOf(item), 1);
          tomb(d, `${fromId}:${vid}`);
        }
        return { ok: true, list: bucketName(d, toId) };
      });
    }
  };

  /**
   * Logged-in visitors: merge the account copy (#rg-saved-server island) into the local one
   * (§4.3, §5.3), then start the account sync when its switch is on (B5).
   *
   * Shared-device safety (rg-saved-sync-2026-10-17): a local copy that came from an account is
   * tagged (OWNER_KEY) with a hash of the customer id rendered by Liquid — never the raw id.
   * No island (logged out) while the copy is tagged, or an island for a different customer →
   * the local copy is cleared first, so the next person never sees or inherits that list.
   * Untagged copies (guests, or sync switched off) behave exactly as before.
   */
  function hydrateFromServer() {
    const island = document.getElementById('rg-saved-server');
    const owner = readOwner();
    if (!island) {
      if (owner) { clearLocal(); doc = emptyDoc(); }
      return;
    }
    const hash = island.getAttribute('data-rg-owner') || '';
    const syncOn = island.getAttribute('data-rg-sync') === 'true' && hash !== '';
    if (owner && owner.o !== hash) { clearLocal(); doc = emptyDoc(); }

    let server = null;
    try { server = normalize(JSON.parse(island.textContent || 'null')); } catch (e) { server = null; }
    if (server) {
      const merged = mergeDocs(server, doc);
      if (!sameContent(merged, doc)) {
        doc = merged; // not a client mutation: rev stays max(server, local)
        writeLocal(doc);
      }
    }
    if (!syncOn) return;

    // Login merge (§4.3): once per session, POST when the local copy holds anything the account
    // lacks (guest saves before signing in). Unsynced changes from an earlier page (d = 1) are
    // always re-sent. After the first 200 the local copy is the merged account copy.
    const prev = owner && owner.o === hash ? owner : null;
    // Both sides in the same canonical (merged, pruned) form before comparing.
    const accountCopy = server ? mergeDocs(server, emptyDoc()) : emptyDoc();
    const loginMerge = sessionGet(MERGED_KEY) !== hash && !sameItems(doc, accountCopy);
    syncQueue.enable(hash, Boolean((prev && prev.d === 1) || loginMerge));
  }

  window.addEventListener('storage', (evt) => {
    if (evt.key !== KEY) return;
    doc = readLocal() || emptyDoc();
    syncQueue.seq += 1;
    emit('storage');
  });
  document.addEventListener('on:bfcache:load-restore', () => {
    doc = readLocal() || doc;
    syncQueue.seq += 1;
    emit('restore');
  });

  // ── Cart helpers: the repeat-add protocol (§3.2 rev 3) ──────────────────────────────────

  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const errorText = (json) => (json && (json.description || json.message)) || (theme.strings && theme.strings.cartError) || t('error');

  const otherProps = (props) => {
    const out = {};
    Object.keys(props || {}).forEach((k) => {
      const v = props[k];
      if (k !== '_rg_p' && v !== '' && v !== null && v !== undefined) out[k] = String(v);
    });
    return out;
  };
  const sameProps = (a, b) => {
    const ka = Object.keys(a).sort();
    const kb = Object.keys(b).sort();
    return ka.length === kb.length && ka.every((k, i) => k === kb[i] && a[k] === b[k]);
  };

  const cart = {
    get() {
      return fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); });
    },

    /**
     * The existing cart line a new add would otherwise SPLIT from: same variant, same non-`_rg_p`
     * properties, but a different (or no) `_rg_p`. Only items that carry `_rg_p` take part, so
     * `_bundle` / `_bundle_offer` / `_gift` lines (never stamped) are never touched. When the
     * `_rg_p` values already match, Shopify merges the add itself — no bump needed.
     */
    mergeTarget(cartData, item) {
      if (!cartData || !item || !item.properties || !item.properties._rg_p || item.selling_plan) return null;
      const want = otherProps(item.properties);
      const id = toInt(item.id);
      const lines = Array.isArray(cartData.items) ? cartData.items : [];
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.variant_id !== id || line.selling_plan_allocation) continue;
        if (!sameProps(otherProps(line.properties), want)) continue;
        if (line.properties && line.properties._rg_p === item.properties._rg_p) return null;
        return line;
      }
      return null;
    },

    /**
     * Bumps a line by KEY with quantity only — never `properties`, which /cart/change.js would
     * overwrite — so the first add's `_rg_p` baseline is kept and the line never splits.
     */
    async change(key, quantity, sections) {
      const body = { id: key, quantity };
      if (sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      let res;
      let json = null;
      try {
        res = await fetch(`${routes.cartChange || '/cart/change'}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
        json = await res.json();
      } catch (e) {
        return { ok: false, message: errorText(null) };
      }
      if (!res.ok) return { ok: false, message: errorText(json), data: json };
      const line = (json.items || []).find((l) => l.key === key) || null;
      const capped = line && line.quantity < quantity
        ? ((theme.strings && theme.strings.cartQtyError) || '').replace('[quantity]', line.quantity)
        : '';
      return { ok: true, data: json, line, capped };
    },

    /**
     * Adds JSON line items with the repeat-add protocol. `sections` (optional) is the theme's
     * comma list for the drawer; the returned `data.sections` comes from the LAST request made.
     * Resolves {ok, data, message} and never throws.
     */
    async add(items, sections) {
      let cartData = null;
      try { cartData = await cart.get(); } catch (e) { cartData = null; }
      const bumps = [];
      const rest = [];
      items.forEach((item) => {
        const line = cart.mergeTarget(cartData, item);
        if (line && !bumps.some((b) => b.line.key === line.key)) bumps.push({ line, item });
        else rest.push(item);
      });
      let last = null;
      let capped = '';
      for (let i = 0; i < bumps.length; i += 1) {
        const { line, item } = bumps[i];
        const isLast = i === bumps.length - 1 && rest.length === 0;
        // eslint-disable-next-line no-await-in-loop
        const res = await cart.change(line.key, line.quantity + Math.max(1, toInt(item.quantity) || 1), isLast ? sections : null);
        if (!res.ok) return res;
        if (res.capped) capped = res.capped;
        last = res.data;
      }
      if (rest.length) {
        const body = { items: rest };
        if (sections) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        let res;
        let json = null;
        try {
          res = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
            method: 'POST',
            headers: { ...jsonHeaders, 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body)
          });
          json = await res.json();
        } catch (e) {
          return { ok: false, message: errorText(null) };
        }
        if (!res.ok) return { ok: false, message: errorText(json), data: json };
        last = json;
      }
      return { ok: true, data: last || {}, capped };
    },

    /**
     * For theme product-form.js (FormData submits): when this add would split an existing line,
     * bump that line instead and return an add.js-shaped result `{ok, data}`; otherwise null and
     * the form posts to /cart/add as usual. `oldCart` is the /cart.js the form already fetched.
     */
    async repeatAddFromForm(oldCart, formData, sections) {
      const properties = {};
      formData.forEach((value, name) => {
        const m = /^properties\[(.+)\]$/.exec(name);
        if (m && typeof value === 'string') properties[m[1]] = value;
      });
      const item = {
        id: toInt(formData.get('id')),
        quantity: Math.max(1, parseInt(formData.get('quantity'), 10) || 1),
        properties,
        selling_plan: formData.get('selling_plan') || null
      };
      const line = cart.mergeTarget(oldCart, item);
      if (!line) return null;
      const res = await cart.change(line.key, line.quantity + item.quantity, sections);
      if (!res.ok) {
        const message = res.message || errorText(null);
        return { ok: false, data: { status: 422, message, description: message } };
      }
      const data = { ...(res.line || { variant_id: item.id, quantity: line.quantity + item.quantity }), sections: res.data.sections };
      if (res.capped) {
        data.status = 422;
        data.description = res.capped;
      }
      return { ok: true, data };
    }
  };

  /** Re-render the cart after a programmatic add (drawer, /cart, or just the header bubble). */
  function refreshCart(opts = {}) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const drawerItems = drawer.querySelector('cart-items');
      // An EMPTY cart's <cart-items> never ran its init(), so its refresh() throws (no drawer
      // reference, no #cart-summary): re-render the whole drawer instead.
      if (drawerItems && drawerItems.dataset.empty === 'true' && typeof drawer.refresh === 'function') drawer.refresh(true);
      else document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      if (opts.open) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true, detail: { opener: opts.opener || null } }));
      }
      return;
    }
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      if (cartItems.dataset.empty === 'false' && typeof cartItems.refresh === 'function') cartItems.refresh();
      else window.location.reload();
      return;
    }
    if (opts.open && theme.settings && theme.settings.afterAtc === 'page') {
      window.location.href = routes.cart || '/cart';
      return;
    }
    fetch(`${window.location.pathname}?sections=cart-icon-bubble`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && json && json['cart-icon-bubble']) bubble.innerHTML = json['cart-icon-bubble'];
      })
      .catch(() => {});
  }

  // ── Announcements + limit messages ──────────────────────────────────────────────────────

  let live = null; // fallback region on <body>, used only while no modal dialog is open

  function makeLive() {
    const el = document.createElement('div');
    el.className = 'visually-hidden';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('data-rg-live', '');
    return el;
  }

  /** The topmost open modal: the list picker first (it sits over the cart drawer), else a drawer. */
  function topDialog() {
    const pickerEl = document.querySelector('rg-saved-picker[open]');
    if (pickerEl) return pickerEl;
    const open = document.querySelectorAll('.drawer[open][aria-modal="true"]');
    return open.length ? open[open.length - 1] : null;
  }

  /**
   * Polite announcement. While an aria-modal drawer or the picker is open, everything outside it
   * is inert to screen readers, so the message goes to a live region INSIDE that dialog (created
   * on first use; the cart drawer's markup is replaced on refresh, so it may be re-created).
   */
  function announce(message) {
    if (!message) return;
    const dialog = topDialog();
    let region = live;
    let fresh = false;
    if (dialog) {
      region = Array.from(dialog.children).find((c) => c.hasAttribute('data-rg-live')) || null;
      if (!region) {
        region = makeLive();
        dialog.appendChild(region);
        fresh = true; // a region must exist before its text changes, so wait a little longer
      }
    }
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, fresh ? 250 : 60);
  }

  // ── Focus management ────────────────────────────────────────────────────────────────────

  /**
   * Focus `el`. Inside an open drawer the theme's focus trap is re-armed around it: trapFocus()
   * collects the focusable elements once, so after the drawer re-renders the old trap points at
   * removed nodes.
   */
  function focusIn(el) {
    if (!el || !el.isConnected) return false;
    const drawer = el.closest('.drawer[open]');
    if (drawer && typeof window.trapFocus === 'function') window.trapFocus(drawer, el);
    else el.focus();
    return document.activeElement === el;
  }

  /** Makes a heading programmatically focusable (tabindex=-1) and returns it. */
  function focusableHeading(el) {
    if (el && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    return el || null;
  }

  /**
   * Where focus goes when the Saved for later section empties and hides: OUTSIDE the section.
   * Cart drawer: its "Your cart" heading (else the close button); /cart: the page heading.
   */
  function sflExitTarget(root) {
    const drawer = root.closest('.drawer');
    if (drawer) {
      return focusableHeading(drawer.querySelector('.drawer__header h2'))
        || drawer.querySelector('.drawer__close-btn, .js-close-drawer')
        || drawer;
    }
    const scope = root.closest('.cc-main-cart') || document;
    return focusableHeading(scope.querySelector('.js-cart-title') || document.querySelector('main h1'));
  }

  /**
   * One-time focus hook for a cart re-render: the theme replaces the cart drawer / cart-items
   * markup after every cart change, which drops focus to <body>. Once `ready()` is true after a
   * DOM change, focus `find()` (or `fallback()`). Unless `force`, focus only moves when it was lost
   * or is still on `interim`, so it is never taken from where the shopper has moved on to. Gives up
   * after `ms`, focusing `fallback()` if focus was lost by then.
   */
  function focusAfterRender({ ready, find, fallback, interim = null, force = false, ms = 8000 }) {
    if (!('MutationObserver' in window)) return;
    let done = false;
    let queued = false;
    let timer = null;
    let observer = null;
    const lost = () => {
      const a = document.activeElement;
      return !a || a === document.body || a === interim;
    };
    const finish = (el, forced) => {
      if (done) return;
      done = true;
      observer.disconnect();
      window.clearTimeout(timer);
      if (el && (forced || lost())) focusIn(el);
    };
    observer = new MutationObserver(() => {
      if (done || queued || !ready()) return;
      queued = true;
      // After the theme's own post-render work (its setFocus, and the drawer scroll restore it
      // runs in a frame + a timeout), so nothing moves focus or scroll after us.
      window.requestAnimationFrame(() => window.setTimeout(() => finish(find() || fallback(), force), 30));
    });
    observer.observe(document.body, { childList: true, subtree: true });
    timer = window.setTimeout(() => finish(fallback(), false), ms);
  }

  function limitMessage(code) {
    const map = {
      list_full: 'listFull',
      lists_full: 'listsFull',
      sfl_full: 'sflFull',
      total_full: 'totalFull',
      name_invalid: 'nameInvalid',
      name_taken: 'nameTaken'
    };
    return t(map[code] || 'error') || t('error');
  }

  // ── Saved-item cards via the Section Rendering API ──────────────────────────────────────

  /**
   * Cards come from /products/<handle>?variant=<id>&sections=rg-saved-card — the theme's own
   * Liquid (snippets/rg-saved-item-card.liquid) in real product context, so every card shows the
   * live title, image, price, sale label and availability. Never `all_products` (the drawer
   * renders on every page, and guests' lists are not visible to Liquid at all). At most 4
   * requests at a time; results are cached for the life of the page, so the drawer re-render
   * after every cart change costs nothing.
   */
  const cards = (() => {
    const cache = new Map(); // key -> outerHTML string, or null when the product is gone (404)
    const pending = new Map();
    const queue = [];
    let active = 0;
    const MAX = 4;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const keyOf = (h, vid) => `${h}|${vid}`;

    function pump() {
      while (active < MAX && queue.length) {
        const job = queue.shift();
        active += 1;
        // No `Accept: application/json` here: with it Shopify answers with the product JSON
        // (`{product: ...}`) instead of the Section Rendering payload, and every card reads as gone.
        fetch(`${root}products/${encodeURIComponent(job.h)}?variant=${job.vid}&sections=rg-saved-card`)
          .then((r) => {
            if (r.status === 404) return null;
            if (!r.ok) throw new Error(String(r.status));
            return r.json();
          })
          .then((json) => {
            let out = null;
            const html = json && json['rg-saved-card'];
            if (html) {
              const tpl = document.createElement('template');
              tpl.innerHTML = html;
              const card = tpl.content.querySelector('[data-rg-saved-item]');
              out = card ? card.outerHTML : null;
            }
            cache.set(job.key, out);
            job.resolve(out);
          })
          .catch(() => job.resolve(undefined)) // transient failure: not cached, retried next render
          .finally(() => {
            active -= 1;
            pending.delete(job.key);
            pump();
          });
      }
    }

    return {
      /** Promise of the card HTML: string, null (gone) or undefined (failed this time). */
      load(h, vid) {
        const key = keyOf(h, vid);
        if (cache.has(key)) return Promise.resolve(cache.get(key));
        if (pending.has(key)) return pending.get(key);
        const promise = new Promise((resolve) => { queue.push({ key, h, vid, resolve }); });
        pending.set(key, promise);
        pump();
        return promise;
      },
      peek(h, vid) {
        const key = keyOf(h, vid);
        return cache.has(key) ? cache.get(key) : undefined;
      },
      build(html) {
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        return tpl.content.firstElementChild;
      }
    };
  })();

  /**
   * Price-change notice on a saved card (§6.3): stored baseline `p` vs the live variant price the
   * card was rendered with. Same currency only; green for a drop (percent added at ≥5%), amber for
   * an increase, nothing when equal. Plain text, so screen readers read the whole sentence.
   */
  function priceChange(card, item) {
    // Wholesale sees its company price; a retail baseline (or none) would be a false notice.
    if (B2B) return null;
    const now = toInt(card.dataset.price);
    if (!item || !(item.p > 0) || !(now >= 0) || item.cur !== card.dataset.currency) return null;
    if (toInt(card.dataset.variantId) !== item.vid) return null;
    if (now < item.p) {
      const diff = item.p - now;
      return { dir: 'drop', diff, pct: Math.floor((diff * 100) / item.p) };
    }
    if (now > item.p) return { dir: 'up', diff: now - item.p, pct: 0 };
    return null;
  }

  function fillNotice(card, item, bucketId) {
    const el = card.querySelector('[data-rg-price-notice]');
    if (!el) return;
    const change = priceChange(card, item);
    el.classList.remove('rg-price-notice--drop', 'rg-price-notice--up');
    if (!change) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    const suffix = bucketId === 'sfl' ? 'Added' : 'Saved';
    if (change.dir === 'drop') {
      const amount = formatMoney(change.diff);
      el.textContent = change.pct >= 5
        ? t(`dropped${suffix}Percent`, { amount, percent: change.pct })
        : t(`dropped${suffix}`, { amount });
      el.classList.add('rg-price-notice--drop');
    } else {
      el.textContent = t(`increased${suffix}`, { old_price: formatMoney(item.p), new_price: formatMoney(toInt(card.dataset.price)) });
      el.classList.add('rg-price-notice--up');
    }
    el.hidden = false;
  }

  /** Sets the context of a fetched card: which bucket it belongs to, availability, notice. */
  function decorate(card, item, bucketId) {
    card.dataset.rgBucket = bucketId;
    card.dataset.rgContext = bucketId === 'sfl' ? 'sfl' : 'list';
    card.dataset.rgVid = String(item.vid);
    const variantGone = toInt(card.dataset.variantId) !== item.vid;
    const unavailable = variantGone || card.dataset.available !== 'true';
    card.classList.toggle('rg-saved-item--unavailable', unavailable);
    if (variantGone) {
      const availability = card.querySelector('[data-rg-availability]');
      if (availability) availability.textContent = t('variantGone');
    }
    const toCart = card.querySelector('[data-rg-action="to-cart"]');
    if (toCart) {
      toCart.disabled = unavailable;
      if (bucketId === 'sfl') {
        toCart.classList.remove('btn--primary');
        toCart.classList.add('btn--secondary');
      }
    }
    fillNotice(card, item, bucketId);
    return card;
  }

  /**
   * A readable name from a product handle, for items whose product is gone (only the handle is
   * stored): "boxing-gloves-16-oz" → "Boxing Gloves 16 Oz".
   */
  function handleName(h) {
    return String(h || '')
      .split('-')
      .filter(Boolean)
      .map((w) => w.charAt(0).toLocaleUpperCase() + w.slice(1))
      .join(' ');
  }

  function placeholderCard(item, bucketId, gone) {
    const card = document.createElement('div');
    card.className = `card card--row rg-saved-item relative flex ${gone ? 'rg-saved-item--gone' : 'rg-saved-item--loading'}`;
    card.dataset.rgSavedItem = '';
    card.dataset.rgBucket = bucketId;
    card.dataset.rgVid = String(item.vid);
    const info = document.createElement('div');
    info.className = 'card__info';
    const name = gone ? handleName(item.h) : '';
    if (name) {
      card.dataset.title = name;
      const title = document.createElement('p');
      title.className = 'h6 regular-text mt-0 mb-1';
      title.textContent = name;
      info.appendChild(title);
    }
    const text = document.createElement('p');
    text.className = 'text-sm mt-0 mb-1';
    text.textContent = gone ? t('gone') : t('loading');
    info.appendChild(text);
    if (gone) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'link text-sm rg-saved-item__gone-remove';
      remove.dataset.rgAction = 'remove';
      remove.textContent = t('itemRemove');
      if (name) remove.setAttribute('aria-label', `${t('itemRemove')}: ${name}`);
      info.appendChild(remove);
    } else {
      card.setAttribute('aria-busy', 'true');
    }
    card.appendChild(info);
    return card;
  }

  function cardFor(item, bucketId, html) {
    if (html === null) return placeholderCard(item, bucketId, true);
    if (typeof html === 'string') {
      const card = cards.build(html);
      if (card) return decorate(card, item, bucketId);
    }
    return placeholderCard(item, bucketId, false);
  }

  /**
   * Renders items into a <ul>. Focus survives the re-render: the same action on the same item
   * when it still exists, else `fallbackFocus` (the list heading), so a removed row never drops
   * keyboard focus to <body>.
   */
  function renderItems(ul, items, bucketId, fallbackFocus) {
    const active = document.activeElement;
    let focusVid = null;
    let focusAction = null;
    if (active && ul.contains(active)) {
      const row = active.closest('[data-rg-row]');
      const action = active.closest('[data-rg-action]');
      focusVid = row ? row.dataset.rgRow : null;
      focusAction = action ? action.dataset.rgAction : null;
    }
    const frag = document.createDocumentFragment();
    items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'rg-saved-list__item';
      li.dataset.rgRow = String(item.vid);
      const cached = cards.peek(item.h, item.vid);
      li.appendChild(cardFor(item, bucketId, cached));
      if (cached === undefined) {
        cards.load(item.h, item.vid).then((html) => {
          if (!li.isConnected || html === undefined) return;
          const focusedHere = li.contains(document.activeElement);
          li.replaceChildren(cardFor(item, bucketId, html));
          if (focusedHere) {
            // Never the image link: it is tabindex=-1 and aria-hidden.
            const first = li.querySelector('[data-rg-action="to-cart"]:not([disabled]), a[href]:not([tabindex="-1"]), button:not([disabled])');
            if (first) focusIn(first);
          }
          ul.dispatchEvent(new CustomEvent('rg:saved:card-ready', { bubbles: true, detail: { vid: item.vid } }));
        });
      }
      frag.appendChild(li);
    });
    ul.replaceChildren(frag);
    if (focusVid) {
      const row = ul.querySelector(`[data-rg-row="${focusVid}"]`);
      const target = row && (row.querySelector(`[data-rg-action="${focusAction}"]:not([disabled])`) || row.querySelector('button:not([disabled]), a[href]:not([tabindex="-1"])'));
      if (target) focusIn(target);
      else if (fallbackFocus) focusIn(fallbackFocus);
    }
  }

  // ── Saved for later (cart drawer + /cart) ───────────────────────────────────────────────

  function renderSfl(root) {
    const ul = root.querySelector('[data-rg-sfl-list]');
    if (!ul) return;
    const items = doc.sfl.slice().sort((a, b) => msOf(b.at) - msOf(a.at));
    // Read before `hidden` is set: a hidden section can hold no focus.
    const hadFocus = root.contains(document.activeElement);
    root.hidden = items.length === 0;
    root.querySelectorAll('[data-rg-sfl-count]').forEach((el) => { el.textContent = String(items.length); });
    // Emptied (the last item removed or moved): focus a target OUTSIDE the now-hidden section.
    let fallback = root.querySelector('[data-rg-sfl-heading]');
    if (!items.length) fallback = hadFocus ? sflExitTarget(root) : null;
    renderItems(ul, items, 'sfl', fallback);
    if (!items.length && hadFocus && fallback && document.activeElement !== fallback) focusIn(fallback);
  }

  // ── Hearts + header count ───────────────────────────────────────────────────────────────

  const heartInfo = (btn) => ({
    vid: toInt(btn.dataset.variantId),
    pid: toInt(btn.dataset.productId),
    h: btn.dataset.handle,
    p: toInt(btn.dataset.price),
    cur: btn.dataset.currency
  });
  // Cards match the PRODUCT (any saved variant fills the heart); the PDP matches the variant.
  const heartMatch = (btn, info) => (btn.dataset.match === 'variant' ? { vid: info.vid } : { pid: info.pid });

  function heartState(btn) {
    const match = heartMatch(btn, heartInfo(btn));
    const def = doc.lists.find((l) => l.def);
    if (def && def.items.some((i) => matches(i, match))) return 'saved';
    return store.listsWith(match).length ? 'elsewhere' : 'none';
  }

  /**
   * State lives in aria-pressed only: "true" = in the default list, "mixed" = only in other
   * lists (activating it opens the picker), "false" = not saved. No `title`: a tooltip that
   * flipped with the state was read out next to the accessible name.
   */
  function paintHeart(btn) {
    const state = heartState(btn);
    btn.dataset.rgState = state;
    btn.setAttribute('aria-pressed', state === 'saved' ? 'true' : state === 'elsewhere' ? 'mixed' : 'false');
    if (state === 'elsewhere') btn.setAttribute('aria-haspopup', 'dialog');
    else btn.removeAttribute('aria-haspopup');
    btn.removeAttribute('title');
    if (state === 'none') {
      const wrap = btn.closest('[data-rg-save-wrap]');
      const el = wrap && wrap.querySelector('[data-rg-save-status]');
      if (el && !el.hidden && !el.contains(document.activeElement)) hideStatus(el);
    }
  }

  /**
   * The header heart has no visible number (Jason 2026-10-17: it drew the eye from the cart
   * count); the count stays in its accessible name only ("Wishlist (3 saved items)").
   */
  function paintHeader() {
    const n = store.count();
    document.querySelectorAll('[data-rg-header-count-label]').forEach((el) => {
      el.textContent = n ? `(${plural(n, 'countOne', 'countOther')})` : '';
    });
  }

  function paintAll() {
    document.querySelectorAll('[data-rg-save]').forEach(paintHeart);
    paintHeader();
  }

  function pop(btn) {
    btn.classList.remove('is-popping');
    void btn.offsetWidth; // restart the animation
    btn.classList.add('is-popping');
    window.setTimeout(() => btn.classList.remove('is-popping'), 400);
  }

  // Confirmations with "View list · Change list" that are showing (at most one at a time).
  const openStatuses = new Set();

  function hideStatus(el) {
    if (!el) return;
    window.clearTimeout(el.rgTimer);
    el.hidden = true;
    openStatuses.delete(el);
  }

  /**
   * The inline "Saved ✓ — View list · Change list" swap (no toast; §6.5). With links it has no
   * timer: "Change list" is a keyboard route to the picker, so it stays until the next
   * interaction with that card (or another heart's confirmation replaces it). A plain message
   * (a limit) hides after 3 s, but not while hovered or focused.
   */
  function showStatus(btn, text, withLinks) {
    const wrap = btn.closest('[data-rg-save-wrap]');
    const el = wrap && wrap.querySelector('[data-rg-save-status]');
    if (!el) return;
    el.replaceChildren();
    const msg = document.createElement('span');
    msg.textContent = text;
    el.appendChild(msg);
    if (withLinks) {
      el.append(' — ');
      const view = document.createElement('a');
      view.className = 'link';
      view.href = S.url || '/pages/wishlist';
      view.textContent = t('viewList');
      el.appendChild(view);
      el.append(' · ');
      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'link rg-save__change';
      change.dataset.rgSaveChange = '';
      change.textContent = t('changeList');
      el.appendChild(change);
    }
    openStatuses.forEach((other) => { if (other !== el) hideStatus(other); });
    el.hidden = false;
    window.clearTimeout(el.rgTimer);
    if (withLinks) {
      openStatuses.add(el);
      return;
    }
    openStatuses.delete(el);
    const hide = () => {
      if (el.matches(':hover') || el.contains(document.activeElement)) {
        el.rgTimer = window.setTimeout(hide, 1000);
        return;
      }
      el.hidden = true;
    };
    el.rgTimer = window.setTimeout(hide, 3000);
  }

  /** A click inside a card (but not on its heart or confirmation) ends that card's confirmation. */
  function endStatusesFor(target) {
    openStatuses.forEach((el) => {
      const wrap = el.closest('[data-rg-save-wrap]');
      if (!el.isConnected || !wrap) {
        openStatuses.delete(el);
        return;
      }
      const scope = wrap.closest('.card, product-form') || wrap.parentElement;
      if (scope && scope.contains(target) && !wrap.contains(target)) hideStatus(el);
    });
  }

  /**
   * The product-image heart (PDP, Featured product) lives in the media gallery, which the theme
   * replaces only on a PRODUCT change — so on a variant change it is rewritten here: the selected
   * variant's id and price, and its title in the accessible name. A confirmation still showing
   * for the previous variant is closed (unless focus is inside it). A null variant (an
   * unavailable combination, or nothing chosen yet) leaves the heart on the last real variant,
   * as the server-rendered fallback did.
   */
  function followVariant(evt) {
    const variant = evt.detail && evt.detail.variant;
    if (!variant || !(toInt(variant.id) > 0)) return;
    const section = evt.target && evt.target.closest ? evt.target.closest('.shopify-section') : null;
    if (!section) return; // the quick-add drawer has no product-image heart
    section.querySelectorAll('[data-rg-save][data-rg-follow-variant]').forEach((btn) => {
      if (toInt(btn.dataset.variantId) === toInt(variant.id)) return;
      btn.dataset.variantId = String(variant.id);
      if (toInt(variant.price) >= 0) btn.dataset.price = String(variant.price);
      const productTitle = btn.dataset.productTitle || btn.dataset.title || '';
      const label = btn.dataset.defaultVariant === 'true' || !variant.title
        ? productTitle
        : `${productTitle} – ${variant.title}`;
      btn.dataset.title = label;
      btn.setAttribute('aria-label', `${t('save')}: ${label}`);
      const wrap = btn.closest('[data-rg-save-wrap]');
      const status = wrap && wrap.querySelector('[data-rg-save-status]');
      if (status && !status.hidden && !status.contains(document.activeElement)) hideStatus(status);
      paintHeart(btn);
    });
  }

  function openPickerForHeart(btn) {
    const info = heartInfo(btn);
    if (!(info.vid > 0)) return;
    picker.open({ mode: 'save', info, match: heartMatch(btn, info), title: btn.dataset.title || '', opener: btn });
  }

  function onHeart(btn) {
    const info = heartInfo(btn);
    if (!(info.vid > 0) || !(info.pid > 0)) return;
    const match = heartMatch(btn, info);
    const def = store.defaultList();
    if (def && def.items.some((i) => matches(i, match))) {
      const res = store.remove(def.id, match);
      if (res.ok) announce(t('announceRemoved', { list: def.name }));
      return;
    }
    if (store.listsWith(match).length) {
      openPickerForHeart(btn); // saved only in other lists: choose (§6.1)
      return;
    }
    const res = store.save(info);
    if (!res.ok) {
      showStatus(btn, limitMessage(res.error), false);
      announce(limitMessage(res.error));
      return;
    }
    pop(btn);
    showStatus(btn, t('saved'), true);
    announce(t('announceSaved', { list: res.list }));
  }

  // ── List picker drawer (SideDrawer subclass: same overlay, close and focus trap) ────────

  const picker = { open() {} };
  const SideDrawerBase = customElements.get('side-drawer');

  if (SideDrawerBase && !customElements.get('rg-saved-picker')) {
    class RgSavedPicker extends SideDrawerBase {
      connectedCallback() {
        if (this.rgBound) return;
        this.rgBound = true;
        this.titleEl = this.querySelector('[data-rg-picker-title]');
        this.itemEl = this.querySelector('[data-rg-picker-item]');
        this.itemTitleEl = this.querySelector('[data-rg-picker-item-title]');
        this.thumbEl = this.querySelector('[data-rg-picker-thumb]');
        this.listEl = this.querySelector('[data-rg-picker-lists]');
        this.noteEl = this.querySelector('[data-rg-picker-note]');
        this.form = this.querySelector('[data-rg-picker-new]');
        this.input = this.form && this.form.querySelector('input');
        this.errorEl = this.querySelector('[data-rg-picker-error]');
        if (this.form) this.form.addEventListener('submit', (evt) => { evt.preventDefault(); this.create(); });
        if (this.input) this.input.addEventListener('input', () => { if (this.input.hasAttribute('aria-invalid')) this.setError(''); });
        // Announcements while the picker is open go here (it is aria-modal; see announce()).
        if (!this.querySelector(':scope > [data-rg-live]')) this.appendChild(makeLive());
        this.listEl.addEventListener('change', (evt) => this.onToggle(evt));
        this.listEl.addEventListener('click', (evt) => this.onChoose(evt));
        document.addEventListener('rg:saved:change', () => { if (this.hasAttribute('open')) this.render(); });
      }

      /** ctx: {mode: 'save'|'move'|'copy', info?, match?, vid?, from?, title, opener, fallback?} */
      openFor(ctx) {
        this.ctx = ctx;
        // Opened from inside another open drawer (Saved for later in the cart drawer)?
        this.parentDrawer = ctx.opener && ctx.opener.closest ? ctx.opener.closest('.drawer[open]') : null;
        const titles = { save: 'pickerSave', move: 'pickerMove', copy: 'pickerCopy' };
        this.titleEl.textContent = t(titles[ctx.mode] || 'pickerSave');
        (this.itemTitleEl || this.itemEl).textContent = ctx.title || '';
        this.itemEl.hidden = !ctx.title;
        this.showThumb(ctx);
        if (this.input) this.input.value = '';
        this.setError('');
        this.render();
        const first = this.listEl.querySelector('input:not([disabled]), button:not([disabled])') || this.input;
        this.open(ctx.opener, first);
      }

      /**
       * The item's image beside its title (Jason 2026-10-17). Taken from the saved-item card the
       * page already has (wishlist page, Saved for later), else from the cached / fetched card
       * HTML for that variant (one Section Rendering request, cached for the page). The image's
       * own alt text is kept; the box keeps its size while it loads, and hides when there is none.
       */
      showThumb(ctx) {
        if (!this.thumbEl) return;
        const token = {};
        this.thumbToken = token;
        this.thumbEl.replaceChildren();
        this.thumbEl.hidden = true;
        const opener = ctx.opener && ctx.opener.closest ? ctx.opener : null;
        const card = opener ? opener.closest('[data-rg-saved-item]') : null;
        const fromCard = card && card.querySelector('.card__media img');
        if (fromCard) {
          this.setThumb(fromCard, ctx.title);
          return;
        }
        const vid = ctx.info ? ctx.info.vid : ctx.vid;
        let h = ctx.info ? ctx.info.h : '';
        if (!h && ctx.from) {
          const item = store.item(ctx.from, vid);
          h = item ? item.h : '';
        }
        if (!h || !(vid > 0)) return;
        this.thumbEl.hidden = false; // reserve the box while the card loads
        this.thumbEl.classList.add('is-loading');
        cards.load(h, vid).then((html) => {
          if (this.thumbToken !== token) return; // reopened for another item meanwhile
          this.thumbEl.classList.remove('is-loading');
          const built = typeof html === 'string' ? cards.build(html) : null;
          const img = built && built.querySelector('.card__media img');
          if (img) this.setThumb(img, ctx.title);
          else this.thumbEl.hidden = true;
        });
      }

      setThumb(source, title) {
        const img = document.createElement('img');
        const src = source.getAttribute('src') || source.currentSrc || '';
        if (!src) return;
        const srcset = source.getAttribute('srcset');
        if (srcset) {
          img.srcset = srcset;
          img.sizes = '64px';
        }
        img.src = src;
        img.alt = source.getAttribute('alt') || title || '';
        img.width = 64;
        img.height = 64;
        img.decoding = 'async';
        this.thumbEl.classList.remove('is-loading');
        this.thumbEl.replaceChildren(img);
        this.thumbEl.hidden = false;
      }

      close(callback) {
        const parent = this.parentDrawer;
        if (this.opener && !this.opener.isConnected) {
          let fallback = this.ctx && this.ctx.fallback && this.ctx.fallback.isConnected ? this.ctx.fallback : null;
          // The last Saved for later item moved away: its heading is hidden with the section.
          const sflRoot = fallback && fallback.closest('[data-rg-sfl]');
          if (sflRoot && sflRoot.hidden) fallback = sflExitTarget(sflRoot);
          this.opener = fallback;
        }
        const opener = this.opener;
        super.close(callback);
        this.parentDrawer = null;
        // SideDrawer.close hid the shared overlay and dropped the focus trap; give both back to the
        // drawer that is still open underneath (e.g. the cart drawer).
        if (parent && parent.hasAttribute('open')) {
          if (this.overlay) this.overlay.classList.add('is-visible');
          if (typeof window.trapFocus === 'function') window.trapFocus(parent, opener && parent.contains(opener) ? opener : parent);
        }
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = !message;
        if (!this.input) return;
        if (message) this.input.setAttribute('aria-invalid', 'true');
        else this.input.removeAttribute('aria-invalid');
      }

      render() {
        const ctx = this.ctx;
        if (!ctx) return;
        const lists = store.lists();
        const frag = document.createDocumentFragment();
        let note = '';
        if (ctx.mode === 'save') {
          // No lists yet: one row for the default list, created on first check.
          const rows = lists.length ? lists : [{ id: '', name: t('defaultListName'), def: true, items: [] }];
          rows.forEach((list) => {
            const li = document.createElement('li');
            li.className = 'rg-picker__row';
            const id = `rg-picker-${list.id || 'default'}`;
            const input = document.createElement('input');
            input.type = 'checkbox';
            input.className = 'checkbox';
            input.id = id;
            input.dataset.listId = list.id;
            input.checked = list.items.some((i) => matches(i, ctx.match));
            const label = document.createElement('label');
            label.htmlFor = id;
            label.className = 'rg-picker__label';
            label.append(this.nameNode(list));
            li.append(input, label);
            frag.appendChild(li);
          });
        } else {
          const targets = lists.filter((l) => l.id !== ctx.from);
          if (!targets.length) note = t('noOtherLists');
          targets.forEach((list) => {
            const li = document.createElement('li');
            li.className = 'rg-picker__row';
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'rg-picker__choice';
            btn.dataset.listId = list.id;
            btn.append(this.nameNode(list));
            li.appendChild(btn);
            frag.appendChild(li);
          });
        }
        this.listEl.replaceChildren(frag);
        if (this.noteEl) {
          this.noteEl.textContent = note;
          this.noteEl.hidden = !note;
        }
        // No other list: the New list field right under the note is the only way on, and is
        // focused first (openFor); the note becomes part of its description.
        if (this.input) {
          const ids = ['rg-picker-error'];
          if (note && this.noteEl && this.noteEl.id) ids.unshift(this.noteEl.id);
          this.input.setAttribute('aria-describedby', ids.join(' '));
        }
      }

      nameNode(list) {
        const span = document.createElement('span');
        span.className = 'rg-picker__name';
        span.textContent = list.name;
        const meta = document.createElement('span');
        meta.className = 'rg-picker__meta text-sm';
        const parts = [];
        if (list.def) parts.push(t('defaultBadge'));
        parts.push(plural(list.items.length, 'itemCountOne', 'itemCountOther'));
        meta.textContent = parts.join(' · ');
        // Screen readers otherwise run the two together ("My WishlistDefault · 1 item").
        const sep = document.createElement('span');
        sep.className = 'visually-hidden';
        sep.textContent = ', ';
        const frag = document.createDocumentFragment();
        frag.append(span, sep, meta);
        return frag;
      }

      onToggle(evt) {
        const input = evt.target.closest('input[type="checkbox"]');
        if (!input || !this.ctx || this.ctx.mode !== 'save') return;
        const listId = input.dataset.listId || undefined;
        const res = input.checked ? store.save(this.ctx.info, listId) : store.remove(listId, this.ctx.match);
        if (!res.ok) {
          input.checked = !input.checked;
          this.setError(limitMessage(res.error));
          return;
        }
        this.setError('');
        announce(t(input.checked ? 'announceSaved' : 'announceRemoved', { list: res.list || '' }));
      }

      onChoose(evt) {
        const btn = evt.target.closest('button[data-list-id]');
        if (!btn || !this.ctx || this.ctx.mode === 'save') return;
        this.transferTo(btn.dataset.listId);
      }

      transferTo(listId) {
        const ctx = this.ctx;
        const res = store.transfer(ctx.vid, ctx.from, listId, ctx.mode === 'copy');
        if (!res.ok) {
          this.setError(limitMessage(res.error));
          return;
        }
        announce(t(ctx.mode === 'copy' ? 'copied' : 'moved', { list: res.list || store.bucketName(listId) }));
        this.close();
      }

      create() {
        const ctx = this.ctx;
        if (!ctx || !this.input) return;
        const res = store.createList(this.input.value);
        if (!res.ok) {
          this.setError(limitMessage(res.error));
          this.input.focus();
          return;
        }
        this.input.value = '';
        this.setError('');
        if (ctx.mode === 'save') {
          const saved = store.save(ctx.info, res.listId);
          if (!saved.ok) this.setError(limitMessage(saved.error));
          else announce(t('announceSaved', { list: res.list }));
          const box = this.listEl.querySelector(`input[data-list-id="${res.listId}"]`);
          if (box) box.focus();
        } else {
          this.transferTo(res.listId);
        }
      }
    }

    customElements.define('rg-saved-picker', RgSavedPicker);
    picker.open = (ctx) => {
      const el = document.querySelector('rg-saved-picker');
      if (el && typeof el.openFor === 'function') el.openFor(ctx);
    };
  }

  // ── Saved-item actions (Saved for later + wishlist page) ────────────────────────────────

  function setItemStatus(card, message) {
    const el = card.querySelector('[data-rg-item-status]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /** add.js with a FRESH `_rg_p` (the card's live price), then out of the bucket, then refresh. */
  async function moveToCart(card, opener) {
    const bucketId = card.dataset.rgBucket;
    const vid = toInt(card.dataset.rgVid);
    const item = store.item(bucketId, vid);
    if (!item) return { ok: false };
    const line = { id: vid, quantity: bucketId === 'sfl' ? item.q || 1 : 1 };
    const price = toInt(card.dataset.price);
    if (!B2B && price >= 0 && card.dataset.currency && toInt(card.dataset.variantId) === vid) {
      line.properties = { _rg_p: `${card.dataset.currency}:${price}` };
    }
    const res = await cart.add([line], null);
    if (!res.ok) return res;
    const sflRoot = bucketId === 'sfl' ? card.closest('[data-rg-sfl]') : null;
    const scope = (sflRoot && sflRoot.closest('.drawer')) || document;
    const stale = new Set(scope.querySelectorAll('.cart-item'));
    // Synchronous re-render: focus moves to the Saved for later heading, or out of the section
    // when this was its last item (renderSfl).
    store.remove(bucketId, { vid });
    if (sflRoot) {
      // Then the cart re-renders: focus the line just added (a NEW node — the old markup is
      // replaced), or the Saved for later heading / the exit target if that cannot be found.
      const freshLine = () => Array.from(scope.querySelectorAll(`.cart-item[data-variant-id="${vid}"]`)).find((el) => !stale.has(el));
      focusAfterRender({
        ready: () => !!freshLine() || Array.from(stale).some((el) => !el.isConnected),
        find: () => {
          const el = freshLine();
          return el && (el.querySelector('.js-item-name') || el.querySelector('a[href]:not([tabindex="-1"]), button:not([disabled]), input:not([type="hidden"])'));
        },
        fallback: () => {
          // The drawer may have re-rendered its whole contents, Saved for later shell included.
          const root = sflRoot.isConnected ? sflRoot : scope.querySelector('[data-rg-sfl]');
          if (root && !root.hidden) return root.querySelector('[data-rg-sfl-heading]');
          if (root) return sflExitTarget(root);
          return scope !== document ? focusableHeading(scope.querySelector('.drawer__header h2')) : null;
        },
        interim: document.activeElement
      });
    }
    refreshCart({ open: bucketId !== 'sfl' && theme.settings && theme.settings.afterAtc === 'drawer', opener });
    return res;
  }

  /** Busy state without `disabled`: disabling the focused button would drop focus to <body>. */
  function setBusy(btn, busy) {
    if (busy) {
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');
    } else {
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-busy');
      btn.classList.remove('is-loading');
    }
  }

  async function onItemAction(btn, card) {
    const action = btn.dataset.rgAction;
    const bucketId = card.dataset.rgBucket;
    const vid = toInt(card.dataset.rgVid);
    if (!bucketId || !(vid > 0)) return;
    if (action === 'remove') {
      const res = store.remove(bucketId, { vid });
      if (res.ok) announce(t('removed'));
      return;
    }
    if (action === 'move' || action === 'copy') {
      const container = card.closest('[data-rg-items-root]');
      picker.open({
        mode: action,
        vid,
        from: bucketId,
        title: card.dataset.title || '',
        opener: btn,
        fallback: container ? container.querySelector('[data-rg-focus-fallback]') : null
      });
      return;
    }
    if (action === 'to-cart') {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return; // unavailable, or busy
      setBusy(btn, true);
      setItemStatus(card, '');
      const res = await moveToCart(card, btn);
      if (!res.ok) {
        setBusy(btn, false);
        setItemStatus(card, res.message || t('error'));
        announce(res.message || t('error'));
        return;
      }
      if (btn.isConnected) setBusy(btn, false);
      announce(t('movedToCart'));
    }
  }

  /** Cart line "Save for later": into the SFL bucket first, then the theme's own remove. */
  function onSaveForLater(btn) {
    const res = store.sflAdd({
      vid: toInt(btn.dataset.variantId),
      pid: toInt(btn.dataset.productId),
      h: btn.dataset.handle,
      q: toInt(btn.dataset.qty),
      p: toInt(btn.dataset.price),
      cur: btn.dataset.currency
    });
    if (!res.ok) {
      const err = document.getElementById(`line-item-error-${btn.dataset.index}`);
      if (err) {
        err.textContent = limitMessage(res.error);
        err.hidden = false;
      }
      return;
    }
    announce(t('sflSaved'));
    const cartItems = btn.closest('cart-items');
    if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
    // The theme's line update blurs the button and re-renders the cart. When the line is gone,
    // focus the new Saved for later card's "Move to cart" (or the section heading while the
    // card is still loading or unavailable). The theme's own Remove is left alone.
    const line = btn.closest('.cart-item');
    const sflRoot = (btn.closest('.drawer') || document).querySelector('[data-rg-sfl]');
    const vid = toInt(btn.dataset.variantId);
    if (line && sflRoot) {
      focusAfterRender({
        ready: () => !line.isConnected,
        find: () => {
          const row = sflRoot.querySelector(`[data-rg-row="${vid}"]`);
          return row && row.querySelector('[data-rg-action="to-cart"]:not([disabled]):not([aria-disabled="true"])');
        },
        fallback: () => (sflRoot.isConnected && !sflRoot.hidden ? sflRoot.querySelector('[data-rg-sfl-heading]') : null),
        force: true // the theme just placed focus itself (first line / empty-cart link); this replaces it
      });
    }
    cartItems.updateQuantity(btn.dataset.index, 0);
  }

  // ── Wiring ──────────────────────────────────────────────────────────────────────────────

  let pressTimer = null;
  let longPressed = false;
  const clearPress = () => { window.clearTimeout(pressTimer); pressTimer = null; };

  // Long-press on a heart = "Save to…" (list picker), §6.1. Keyboard users get the same picker
  // from Shift+F10 / the ContextMenu key on a focused heart (aria-keyshortcuts) and from the
  // "Change list" button in the inline confirmation, which stays until the next interaction.
  document.addEventListener('pointerdown', (evt) => {
    const btn = evt.target.closest && evt.target.closest('[data-rg-save]');
    longPressed = false;
    if (!btn || evt.button > 0) return;
    clearPress();
    pressTimer = window.setTimeout(() => {
      longPressed = true;
      openPickerForHeart(btn);
    }, 550);
  }, { passive: true });
  ['pointerup', 'pointercancel'].forEach((type) => document.addEventListener(type, clearPress, { passive: true }));
  window.addEventListener('scroll', clearPress, { passive: true });
  document.addEventListener('contextmenu', (evt) => {
    if (evt.target.closest && evt.target.closest('[data-rg-save]')) evt.preventDefault();
  });

  document.addEventListener('keydown', (evt) => {
    const target = evt.target;
    if (!target || !target.closest) return;
    const heart = target.closest('[data-rg-save]');
    if (heart && (evt.key === 'ContextMenu' || (evt.key === 'F10' && evt.shiftKey))) {
      evt.preventDefault();
      openPickerForHeart(heart);
      return;
    }
    if (evt.key === 'Escape') {
      const wrap = target.closest('[data-rg-save-wrap]');
      const el = wrap && wrap.querySelector('[data-rg-save-status]');
      if (!el || el.hidden) return;
      const inStatus = el.contains(target);
      hideStatus(el);
      if (inStatus) {
        const btn = wrap.querySelector('[data-rg-save]');
        if (btn) btn.focus();
      }
    }
  });

  document.addEventListener('click', (evt) => {
    const target = evt.target;
    if (!target.closest) return;
    if (openStatuses.size) endStatusesFor(target);

    const heart = target.closest('[data-rg-save]');
    if (heart) {
      evt.preventDefault();
      if (longPressed) { longPressed = false; return; }
      onHeart(heart);
      return;
    }

    const change = target.closest('[data-rg-save-change]');
    if (change) {
      const wrap = change.closest('[data-rg-save-wrap]');
      const btn = wrap && wrap.querySelector('[data-rg-save]');
      if (btn) openPickerForHeart(btn);
      return;
    }

    const sfl = target.closest('[data-rg-sfl-save]');
    if (sfl) {
      evt.preventDefault();
      onSaveForLater(sfl);
      return;
    }

    const action = target.closest('[data-rg-action]');
    const card = action && action.closest('[data-rg-saved-item]');
    if (action && card) {
      evt.preventDefault();
      onItemAction(action, card);
    }
  });

  function renderAllSfl() {
    document.querySelectorAll('[data-rg-sfl]').forEach(renderSfl);
  }

  document.addEventListener('rg:saved:change', () => {
    paintAll();
    renderAllSfl();
  });

  document.addEventListener('on:variant:change', followVariant);

  // Cards, the cart drawer and the PDP buy box re-render without one shared event (facets,
  // load-more, recommendations, variant changes, every cart change) — watch for new nodes.
  const WATCH = '[data-rg-save], [data-rg-sfl], [data-rg-header-count-label]';
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      paintAll();
      document.querySelectorAll('[data-rg-sfl]').forEach((root) => {
        if (root.dataset.rgReady) return;
        root.dataset.rgReady = 'true';
        renderSfl(root);
      });
    });
  };

  function init() {
    live = makeLive();
    document.body.appendChild(live);

    hydrateFromServer();
    paintAll();
    document.querySelectorAll('[data-rg-sfl]').forEach((root) => {
      root.dataset.rgReady = 'true';
      renderSfl(root);
    });

    if ('MutationObserver' in window) {
      new MutationObserver((mutations) => {
        for (let i = 0; i < mutations.length; i += 1) {
          const added = mutations[i].addedNodes;
          for (let j = 0; j < added.length; j += 1) {
            const node = added[j];
            if (node.nodeType === 1 && (node.matches(WATCH) || node.querySelector(WATCH))) {
              schedule();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.rgSaved = {
    store,
    cart,
    cards,
    picker,
    syncQueue,
    decorate,
    renderItems,
    priceChange,
    refreshCart,
    moveToCart,
    announce,
    focusIn,
    handleName,
    limitMessage,
    formatMoney,
    t,
    plural
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
